/**
 * Reservoir — Analytics tracker.
 *
 * Fires a single `pageview` event to the backend on every route change.
 * Captures UTM params, persists first-touch attribution for 30 days, and
 * tags every event with a stable anonymous session id.
 *
 * Usage (App.js):
 *
 *     import { initAnalytics, useAnalyticsRouter } from "@/lib/useAnalyticsTracker";
 *
 *     // once at boot
 *     useEffect(() => { initAnalytics(); }, []);
 *
 *     // inside a <BrowserRouter> child, so it has access to the location
 *     function AppRouter() {
 *       useAnalyticsRouter();  // fires pageview on every route change
 *       // ...
 *     }
 *
 * Backend ingest: POST /api/analytics/event
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const BACKEND  = process.env.REACT_APP_BACKEND_URL || "";
const SID_KEY  = "rsvr_anon_id";
const ATTR_KEY = "rsvr_attribution";
const ATTR_TTL_DAYS = 30;

// ---------- ids + attribution -----------------------------------------

function getOrCreateSessionId() {
  if (typeof window === "undefined") return "";
  try {
    let sid = window.localStorage.getItem(SID_KEY);
    if (!sid) {
      sid = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch { return ""; }
}

function parseUtmFromUrl() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const get = (k) => (p.get(k) || "").trim().slice(0, 128) || null;
  const out = {
    utm_source:   get("utm_source"),
    utm_medium:   get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_content:  get("utm_content"),
    utm_term:     get("utm_term"),
  };
  return Object.values(out).some(Boolean) ? out : null;
}

/** First-touch attribution: store the UTM the visitor first arrived with. */
function maybeRecordFirstTouch() {
  if (typeof window === "undefined") return;
  try {
    const fresh = parseUtmFromUrl();
    if (!fresh) return;
    const existing = window.localStorage.getItem(ATTR_KEY);
    if (existing) {
      try {
        const { ts } = JSON.parse(existing);
        if (Date.now() - ts < ATTR_TTL_DAYS * 86400 * 1000) return; // keep first-touch
      } catch { /* corrupt; overwrite */ }
    }
    window.localStorage.setItem(ATTR_KEY, JSON.stringify({ utm: fresh, ts: Date.now() }));
  } catch { /* ignore */ }
}

export function getStoredAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTR_KEY);
    if (!raw) return null;
    const { utm, ts } = JSON.parse(raw);
    if (!utm || Date.now() - ts > ATTR_TTL_DAYS * 86400 * 1000) return null;
    return utm;
  } catch { return null; }
}

// ---------- public API -------------------------------------------------

/** Call once at app boot. Sets up session id + records first-touch. */
export function initAnalytics() {
  getOrCreateSessionId();
  maybeRecordFirstTouch();
}

/** Fire one pageview event for the current location. */
export async function trackPageview(path) {
  if (typeof window === "undefined") return;
  const sid = getOrCreateSessionId();
  if (!sid) return;
  const utm = parseUtmFromUrl() || getStoredAttribution() || {};
  const body = {
    event_type: "pageview",
    path:       path || window.location.pathname + window.location.search,
    referrer:   document.referrer || null,
    session_id: sid,
    ...utm,
  };
  try {
    // navigator.sendBeacon falls back gracefully on older browsers via fetch.
    const url = `${BACKEND}/api/analytics/event`;
    const json = JSON.stringify(body);
    if (navigator.sendBeacon) {
      const blob = new Blob([json], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json,
        keepalive: true,
      }).catch(() => {});
    }
  } catch { /* ignore — analytics must never break the page */ }
}

/** React hook: fires a pageview every time the route changes. */
export function useAnalyticsRouter() {
  const location = useLocation();
  const lastPath = useRef(null);
  useEffect(() => {
    const p = location.pathname + location.search;
    if (p === lastPath.current) return;
    lastPath.current = p;
    trackPageview(p);
  }, [location.pathname, location.search]);
}
